import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
    Contact,
    Counterparty,
    CounterpartyType,
    useCounterpartyAsyncAutocompleteOptions,
    useGetCounterpartyByIdQuery,
} from 'entities/counterparties/model'
import { useNumberMask } from 'shared/utils/useNumberMask'
import {
    selectBookingCounterparty,
    selectContactId,
    setContactId,
} from '../slices'
import { AutocompleteOption } from 'shared/components/Autocomplete'
import { CLIENTSPROFILE } from 'shared/consts/routingConsts'

const CounterpartyOptionName = {
    phone: 'phone',
    name: 'name',
    inn: 'inn',
    email: 'email',
    fio: 'contacts',

    contactsPhone: 'contactsPhone',
    contactsEmail: 'contactsEmail',
    contactsFio: 'contactsFio',
} as const

type CounterpartyOption = {
    [CounterpartyOptionName.phone]: string
    [CounterpartyOptionName.name]: string
    [CounterpartyOptionName.inn]: string
    [CounterpartyOptionName.email]: string
    [CounterpartyOptionName.fio]: string

    [CounterpartyOptionName.contactsPhone]: string
    [CounterpartyOptionName.contactsEmail]: string
    [CounterpartyOptionName.contactsFio]: string
}

const initValue = {
    [CounterpartyOptionName.phone]: '',
    [CounterpartyOptionName.name]: '',
    [CounterpartyOptionName.inn]: '',
    [CounterpartyOptionName.email]: '',
    [CounterpartyOptionName.fio]: '',

    [CounterpartyOptionName.contactsPhone]: '',
    [CounterpartyOptionName.contactsEmail]: '',
    [CounterpartyOptionName.contactsFio]: '',
} as const

export type CounterpartyOptionNameType =
    (typeof CounterpartyOptionName)[keyof typeof CounterpartyOptionName]

const countryCode = '+7'
const phoneMask = `${countryCode}-000-000-00-00` as const

const buildCounterpartyOption = (counterpartyToBuild: Counterparty) => ({
    [CounterpartyOptionName.phone]: {
        value: counterpartyToBuild.id.toString(),
        label: counterpartyToBuild.phone,
        payload: counterpartyToBuild,
    },
    [CounterpartyOptionName.name]: {
        value: counterpartyToBuild.id.toString(),
        label:
            counterpartyToBuild.additionalData.documents?.organizationName ??
            '',
        payload: counterpartyToBuild,
    },
    [CounterpartyOptionName.fio]: {
        value: counterpartyToBuild.id.toString(),
        label: [
            counterpartyToBuild?.contact?.surname,
            counterpartyToBuild?.contact?.firstName,
            counterpartyToBuild?.contact?.patronymic,
        ].join(' '),
        payload: counterpartyToBuild,
    },
    [CounterpartyOptionName.inn]: {
        value: counterpartyToBuild.id.toString(),
        label: counterpartyToBuild.additionalData.documents?.inn ?? '',
        payload: counterpartyToBuild,
    },
    [CounterpartyOptionName.email]: {
        value: counterpartyToBuild.id.toString(),
        label: counterpartyToBuild.email ?? '',
        payload: counterpartyToBuild,
    },
})

const buildContactsOptions = (
    contactToBuild: Contact,
    currentCounterpartyObject: Counterparty,
) => {
    return {
        [CounterpartyOptionName.phone]: {
            value: contactToBuild.id.toString(),
            label: contactToBuild.phone ?? currentCounterpartyObject.phone,
            payload: currentCounterpartyObject,
        },
        [CounterpartyOptionName.email]: {
            value: contactToBuild.id.toString(),
            label: contactToBuild.email ?? currentCounterpartyObject.email,
            payload: currentCounterpartyObject,
        },
        [CounterpartyOptionName.fio]: {
            value: contactToBuild.id.toString(),
            label:
                [
                    contactToBuild?.surname,
                    contactToBuild?.firstName,
                    contactToBuild?.patronymic,
                ].join(' ') ??
                [
                    currentCounterpartyObject?.contact?.surname,
                    currentCounterpartyObject?.contact?.firstName,
                    currentCounterpartyObject?.contact?.patronymic,
                ].join(' '),
            payload: currentCounterpartyObject,
        },
    }
}

const defaultCounterpartyObject = {
    [CounterpartyOptionName.phone]: null,
    [CounterpartyOptionName.name]: null,
    [CounterpartyOptionName.inn]: null,
    [CounterpartyOptionName.email]: null,
    [CounterpartyOptionName.fio]: null,
}

export const useCounterpartiesOptions = (isCompany?: boolean) => {
    const counterparty = useSelector(selectBookingCounterparty)
    const dispatch = useDispatch()
    const contactId = useSelector(selectContactId)
    const counterpartyId = counterparty?.id ?? null

    const { onInput, onKeyDown } = useNumberMask(phoneMask, countryCode)

    const [optionInputValue, setOptionInputValue] =
        useState<CounterpartyOption>(initValue)

    const {
        counterparty: currentCounterpartyObject,
        isFetching: isFetchingCounterpartyObject,
    } = useGetCounterpartyByIdQuery(counterpartyId ?? null)

    const onChangeCounterpartyOption = useCallback(
        (name: CounterpartyOptionNameType) => (value: string) => {
            setOptionInputValue(prev => ({
                ...prev,
                [name]: value,
            }))
        },
        [],
    )

    const searchString = useMemo(
        () =>
            optionInputValue.phone?.length
                ? optionInputValue.phone
                : optionInputValue.name?.length
                  ? optionInputValue.name
                  : optionInputValue.inn?.length
                    ? optionInputValue.inn
                    : optionInputValue.contactsPhone?.length
                      ? optionInputValue.contactsPhone
                      : optionInputValue.contactsEmail?.length
                        ? optionInputValue.contactsEmail
                        : optionInputValue.contactsFio?.length
                          ? optionInputValue.contactsFio
                          : undefined,
        [optionInputValue],
    )

    const {
        counterparties,
        fetchNextPage,
        isFetching: isFetchingCounterparties,
    } = useCounterpartyAsyncAutocompleteOptions({
        search_param: searchString,
        type_filter: isCompany
            ? CounterpartyType.Company
            : CounterpartyType.Person,
    })

    const counterpartiesOptions = useMemo(() => {
        if (!counterparties?.length) {
            return {
                [CounterpartyOptionName.phone]: [],
                [CounterpartyOptionName.name]: [],
                [CounterpartyOptionName.inn]: [],
                [CounterpartyOptionName.email]: [],
                [CounterpartyOptionName.fio]: [],
            }
        }

        const options = counterparties.map(buildCounterpartyOption)

        return {
            [CounterpartyOptionName.phone]: options.map(
                opt => opt[CounterpartyOptionName.phone],
            ),
            [CounterpartyOptionName.name]: options.map(
                opt => opt[CounterpartyOptionName.name],
            ),
            [CounterpartyOptionName.fio]: options.map(
                opt => opt[CounterpartyOptionName.fio],
            ),
            [CounterpartyOptionName.inn]: options.map(
                opt => opt[CounterpartyOptionName.inn],
            ),
            [CounterpartyOptionName.email]: options.map(
                opt => opt[CounterpartyOptionName.email],
            ),
        }
    }, [counterparties])

    const currentCounterpartyOption = useMemo(
        () =>
            currentCounterpartyObject
                ? buildCounterpartyOption(currentCounterpartyObject)
                : defaultCounterpartyObject,
        [currentCounterpartyObject],
    )

    const currentCounterparty = useMemo(
        () => ({
            email: currentCounterpartyObject?.email,
            name: [
                currentCounterpartyObject?.contact?.surname,
                currentCounterpartyObject?.contact?.firstName,
                currentCounterpartyObject?.contact?.patronymic,
            ].join(' '),
            contactEmail: currentCounterpartyObject?.contact?.email,
            contactPhone: currentCounterpartyObject?.contact?.phone,
            organizationName:
                currentCounterpartyObject?.additionalData?.documents
                    ?.organizationName,
            inn: currentCounterpartyObject?.additionalData?.documents?.inn,
        }),
        [currentCounterpartyObject],
    )

    const onChangeNumber = useCallback(
        (value: string) => {
            const changeCallback = onChangeCounterpartyOption('phone')

            if (value.length < (optionInputValue.phone?.length ?? 0)) {
                changeCallback(onKeyDown(value))
                return
            }
            changeCallback(onInput(value))
        },
        [optionInputValue, onChangeCounterpartyOption],
    )

    const onChangeName = useCallback(
        (value: string) => {
            const changeCallback = onChangeCounterpartyOption('name')
            changeCallback(value)
        },
        [onChangeCounterpartyOption],
    )

    const onChangeInn = useCallback(
        (value: string) => {
            const changeCallback = onChangeCounterpartyOption('inn')
            changeCallback(value)
        },
        [onChangeCounterpartyOption],
    )

    const onChangeEmail = useCallback(
        (value: string) => {
            const changeCallback = onChangeCounterpartyOption('email')
            changeCallback(value)
        },
        [onChangeCounterpartyOption],
    )

    const onChangeContactName = useCallback(
        (value: string) => {
            const changeCallback = onChangeCounterpartyOption('name')
            changeCallback(value)
        },
        [onChangeCounterpartyOption],
    )

    const onChangeContactsPhone = useCallback(
        (value: string) => {
            const changeCallback = onChangeCounterpartyOption('contactsPhone')

            if (value.length < (optionInputValue.phone?.length ?? 0)) {
                changeCallback(onKeyDown(value))
                return
            }
            changeCallback(onInput(value))
        },
        [onChangeCounterpartyOption],
    )
    const onChangeContactsEmail = useCallback(
        (value: string) => {
            const changeCallback = onChangeCounterpartyOption('contactsEmail')
            changeCallback(value)
        },
        [onChangeCounterpartyOption],
    )
    const onChangeContactsFio = useCallback(
        (value: string) => {
            const changeCallback = onChangeCounterpartyOption('contactsFio')
            changeCallback(value)
        },
        [onChangeCounterpartyOption],
    )

    const [contactOptionValue, setContactOptionValue] =
        useState<AutocompleteOption<Counterparty>>()

    console.log(currentCounterpartyObject, contactId)
    const getContactFromCounterparty = (contactId?: number | string | null) => {
        return currentCounterpartyObject.contacts.find(
            c => contactId?.toString() === c.id.toString(),
        )
    }

    const currentContactOptionValue = useMemo(() => {
        if (!currentCounterpartyObject) {
            return defaultCounterpartyObject
        }

        const currentContact = getContactFromCounterparty(
            contactOptionValue?.value ?? contactId,
        )
        if (currentContact && currentCounterpartyObject) {
            return buildContactsOptions(
                currentContact,
                currentCounterpartyObject,
            )
        }
        return defaultCounterpartyObject
    }, [contactOptionValue, currentCounterpartyObject, contactId])

    const contactsOptions = useMemo(() => {
        const options = (counterparties ?? [])
            ?.filter(counterparty =>
                currentCounterpartyObject
                    ? counterparty.id === currentCounterpartyObject.id
                    : true,
            )
            ?.flatMap(counterparty =>
                counterparty.contacts.map(contact =>
                    buildContactsOptions(contact, counterparty),
                ),
            )

        return {
            [CounterpartyOptionName.phone]: options.map(
                opt => opt[CounterpartyOptionName.phone],
            ),
            [CounterpartyOptionName.email]: options.map(
                opt => opt[CounterpartyOptionName.email],
            ),
            [CounterpartyOptionName.fio]: options.map(
                opt => opt[CounterpartyOptionName.fio],
            ),
        }
    }, [counterparties, currentCounterpartyObject])

    const counterpartyRedirectUrl = currentCounterpartyObject
        ? `${CLIENTSPROFILE}/${currentCounterpartyObject.id}`
        : null

    const updateContactValue = (option: AutocompleteOption<Counterparty>) => {
        setContactOptionValue(option)
        dispatch(setContactId({ contactId: Number(option.value) }))
    }

    return {
        optionInputValue,
        currentCounterpartyOption,
        counterpartiesOptions,
        currentCounterparty,
        fetchNextPage,
        isFetching: isFetchingCounterparties || isFetchingCounterpartyObject,
        onChangeNumber,
        onChangeName,
        onChangeInn,
        onChangeEmail,
        onChangeContactName,

        currentContactOptionValue,
        updateContactValue,
        contactsOptions,
        onChangeContactsPhone,
        onChangeContactsEmail,
        onChangeContactsFio,

        counterpartyRedirectUrl,
    }
}
